/**
 * MethodScript validation module.
 * Handles semantic validation using C# WASM and error display in Monaco.
 */

import * as monaco from 'monaco-editor';
import { logger } from 'shared-monaco-methodscript';

/**
 * C# DiagnosticSeverity values
 * @see https://learn.microsoft.com/dotnet/api/microsoft.codeanalysis.diagnosticseverity
 */
const CSHARP_SEVERITY_ERROR = 1;
const CSHARP_SEVERITY_WARNING = 2;
const CSHARP_SEVERITY_INFO = 3;
const CSHARP_SEVERITY_HINT = 4;

/**
 * Diagnostic error from validation
 */
export interface ValidationError {
    line: number;
    column: number;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'hint';
    code?: string;
    fix?: unknown; // Code fix data if available
}

/**
 * Status update callback type
 */
export type StatusCallback = (message: string, isError: boolean) => void;

/**
 * Store for current diagnostics (used by code actions)
 */
let currentDiagnostics: ValidationError[] = [];

/**
 * Get the current diagnostics
 */
export function getCurrentDiagnostics(): ValidationError[] {
    return currentDiagnostics;
}

/**
 * Set the current diagnostics
 */
export function setCurrentDiagnostics(diagnostics: ValidationError[]): void {
    currentDiagnostics = diagnostics;
}

// Declare global window properties used for WASM validation
declare global {
    interface Window {
        MethodScriptFormatterWASM?: {
            applyFormatting(text: string): Promise<string>;
            validateMethodScript(code: string): Promise<WasmDiagnostic[]>;
        };
        currentDiagnostics?: ValidationError[];
    }
}

/**
 * WASM diagnostic returned from C# validation (camelCase via JsonNamingPolicy.CamelCase)
 */
interface WasmDiagnostic {
    range?: WasmRange;
    message?: string;
    code?: string;
    fix?: unknown;
    severity?: number;
}

/**
 * Range object in WASM diagnostic
 */
interface WasmRange {
    start?: WasmPosition;
    end?: WasmPosition;
}

/**
 * Position object in WASM diagnostic
 */
interface WasmPosition {
    line?: number;
    character?: number;
}

/**
 * Perform semantic validation using C# WASM
 * @param code - The MethodScript code to validate
 * @returns Array of validation errors
 */
export async function performSemanticValidation(code: string): Promise<ValidationError[]> {
    const errors: ValidationError[] = [];

    // Call C# WASM validation
    try {
        if (!window.MethodScriptFormatterWASM) {
            logger.warn('MethodScriptFormatterWASM not available');
            return errors;
        }

        const diagnostics = await window.MethodScriptFormatterWASM.validateMethodScript(code);

        // Convert C# diagnostics to editor error format
        for (const diagnostic of diagnostics) {
            const start = diagnostic.range?.start;
            const message = diagnostic.message || 'Unknown error';
            const diagnosticCode = diagnostic.code;
            const fix = diagnostic.fix;
            const csharpSeverity = diagnostic.severity ?? 1;

            // Map C# DiagnosticSeverity: Error=1, Warning=2, Information=3, Hint=4
            let severity: 'error' | 'warning' | 'info' | 'hint' = 'error';
            if (csharpSeverity === CSHARP_SEVERITY_WARNING) {
                severity = 'warning';
            } else if (csharpSeverity === CSHARP_SEVERITY_INFO) {
                severity = 'info';
            } else if (csharpSeverity === CSHARP_SEVERITY_HINT) {
                severity = 'hint';
            }

            errors.push({
                line: (start?.line ?? 0) + 1, // C# uses 0-indexed, editor uses 1-indexed
                column: start?.character ?? 0,
                message: message,
                severity: severity,
                code: diagnosticCode,
                fix: fix, // Include code fix if available
            });
        }
    } catch (error) {
        logger.error('WASM validation error:', error);
        // Fall back to no semantic validation if WASM fails
    }

    return errors;
}

/**
 * Improve ANTLR error messages to be more user-friendly
 * @param msg - The original error message
 * @param _offendingSymbol - The offending symbol (unused but kept for API compatibility)
 * @returns Improved error message
 */
export function improveErrorMessage(msg: string, _offendingSymbol?: unknown): string {
    // Handle "extraneous input" errors
    if (msg.includes('extraneous input')) {
        const match = msg.match(/extraneous input '([^']+)'/);
        if (match) {
            const invalidToken = match[1];
            return `Invalid command or syntax: '${invalidToken}'`;
        }
    }

    // Handle "mismatched input" errors
    if (msg.includes('mismatched input')) {
        const match = msg.match(/mismatched input '([^']+)'/);
        if (match) {
            const invalidToken = match[1];
            return `Unexpected token: '${invalidToken}'`;
        }
    }

    // Handle "missing" errors
    if (msg.includes('missing')) {
        const match = msg.match(/missing '([^']+)'/);
        if (match) {
            const expectedToken = match[1];
            return `Missing: '${expectedToken}'`;
        }
    }

    // Handle "no viable alternative" errors
    if (msg.includes('no viable alternative')) {
        return 'Syntax error: unexpected input';
    }

    // If the message contains a long "expecting" list, simplify it
    if (msg.includes('expecting {')) {
        // Extract just the first part before "expecting"
        const beforeExpecting = msg.split('expecting')[0].trim();
        return beforeExpecting || 'Syntax error';
    }

    // Return original message if no pattern matches
    return msg;
}

/**
 * Display errors as Monaco markers
 * @param editor - The Monaco editor instance
 * @param errors - Array of validation errors
 * @param monacoInstance - The Monaco instance
 */
export function displayErrors(editor: monaco.editor.IStandaloneCodeEditor, errors: ValidationError[], monacoInstance: typeof monaco): void {
    if (!editor) return;

    // Store diagnostics for code action provider
    currentDiagnostics = errors;
    window.currentDiagnostics = errors; // Update global reference

    const model = editor.getModel();
    if (!model) return;

    const markers = errors.map((error) => {
        // Map severity: error, warning, info
        let severity = monacoInstance.MarkerSeverity.Error;
        if (error.severity === 'warning') {
            severity = monacoInstance.MarkerSeverity.Warning;
        } else if (error.severity === 'info') {
            severity = monacoInstance.MarkerSeverity.Info;
        }

        return {
            severity: severity,
            startLineNumber: error.line,
            startColumn: error.column + 1,
            endLineNumber: error.line,
            endColumn: model.getLineLength(error.line) + 1, // Monaco columns are 1-based
            message: error.message,
            code: error.code, // Include error code for code actions
        };
    });

    monacoInstance.editor.setModelMarkers(model, 'methodscript', markers);
}

/**
 * Validate MethodScript code and update the editor
 * @param editor - The Monaco editor instance
 * @param monacoInstance - The Monaco instance
 * @param updateStatus - Callback to update status display
 */
export async function validateCode(editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco, updateStatus: StatusCallback): Promise<void> {
    if (!editor) {
        return;
    }

    const code = editor.getValue();

    try {
        // C# WASM handles ALL validation (syntax + semantic)
        const diagnostics = await performSemanticValidation(code);

        // Display diagnostics in Monaco
        displayErrors(editor, diagnostics, monacoInstance);

        // Log diagnostics for debugging
        if (diagnostics.length > 0) {
            logger.debug('Validation errors:', JSON.stringify(diagnostics, null, 2));
        }

        // Count errors and warnings separately
        const errorCount = diagnostics.filter((d) => d.severity === 'error').length;
        const warningCount = diagnostics.filter((d) => d.severity === 'warning').length;

        // Update status - only actual errors count as errors
        if (errorCount === 0 && warningCount === 0) {
            updateStatus('✓ No errors', false);
        } else if (errorCount === 0) {
            updateStatus(`✓ ${warningCount} warning(s)`, false);
        } else if (warningCount === 0) {
            updateStatus(`✗ ${errorCount} error(s)`, true);
        } else {
            updateStatus(`✗ ${errorCount} error(s), ${warningCount} warning(s)`, true);
        }

        // Update send button styling based on actual errors only (warnings are OK)
        updateSendButtonValidState(errorCount === 0);
    } catch (error) {
        logger.error('Validation error:', error);
        updateStatus('Validation failed', true);
        updateSendButtonValidState(false);
    }
}

/**
 * Update the send button's valid state styling
 * @param isValid - Whether the script has no errors
 */
export function updateSendButtonValidState(isValid: boolean): void {
    const sendBtn = document.getElementById('deviceSendBtn');
    if (sendBtn) {
        if (isValid) {
            sendBtn.classList.add('valid');
        } else {
            sendBtn.classList.remove('valid');
        }
    }
}
